import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { GalleryEmptyState } from "@/components/content/gallery-empty-state"
import { GalleryFilteredEmptyState } from "@/components/content/gallery-filtered-empty-state"
import { GalleryFilters } from "@/components/content/gallery-filters"
import { GalleryGrid } from "@/components/content/gallery-grid"
import { GalleryPagination } from "@/components/content/gallery-pagination"
import { useGalleryUrl } from "@/hooks/use-gallery-url"
import AppLayout from "@/layouts/app-layout"
import type { ContentGalleryProps } from "@/types/content-gallery"

const PER_PAGE = 24 // mirrors Brand::ContentGalleriesController::PER_PAGE

/**
 * OKS-39 — Brand Content Gallery (`/brand/content`).
 *
 * Read-only grid of approved Submissions across all of `Current.brand`'s
 * missions, with mission/creator/date filters. Single Inertia page,
 * partial-reload driven by `useGalleryUrl`.
 *
 * 5-state matrix (UX Spec §2):
 *   - Empty (brand has 0 approved submissions ever) → `<GalleryEmptyState>`,
 *     filter row hidden.
 *   - Loading → handled by Inertia partial reload (skeleton not shipped
 *     in V1; first paint is SSR).
 *   - Error → `router.on('error')` is the global handler; Inertia banner.
 *   - Success → filter row + grid + pagination.
 *   - Filtered-empty (brand has content, current filters return zero) →
 *     `<GalleryFilteredEmptyState>` with Reset CTA, filter row stays.
 *
 * Brand isolation: server-side floor is `authorized_scope(Mission.all)`;
 * the FE never sees cross-brand rows.
 */
export default function ContentGallery({
  submissions,
  pagination,
  filter_options,
  applied_filters,
  is_empty,
}: ContentGalleryProps) {
  const { t } = useTranslation()
  const url = useGalleryUrl(applied_filters)

  return (
    <AppLayout>
      <Head title={t("content_gallery.title")} />
      <div className="flex flex-col gap-6 p-6">
        <header className="flex flex-col gap-1">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("content_gallery.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("content_gallery.subtitle")}
          </p>
        </header>

        {is_empty ? (
          <GalleryEmptyState />
        ) : (
          <>
            <GalleryFilters
              filterOptions={filter_options}
              applied={applied_filters}
              onFilterChange={url.setFilter}
              onResetAll={url.clearAll}
            />

            {pagination.count === 0 ? (
              <GalleryFilteredEmptyState onReset={url.clearAll} />
            ) : (
              <>
                <GalleryGrid rows={submissions} />
                <GalleryPagination
                  pagination={pagination}
                  perPage={PER_PAGE}
                  onPageChange={url.setPage}
                />
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}
